import { Input, Label, TextArea } from '@components';
import { NAME_MAX_LENGTH } from '@constants/generic';
import { nameRegex } from '@constants/regex';
import SupportingDocumentUpload, {
  UploadingDocument
} from '@features/branch-management/components/supporting-document-upload/SupportingDocumentUpload';

import BusinessLicenseDocument from './BusinessLicenseDocument';
import PanDocument from './PanDocument';

const REMARKS_MAX_LENGTH = 1000;

interface PanCardDetailsProps {
  pan: string;
  onPanChange: (value: string) => void;
  panError: string;
  panFile: File | null;
  panFileRef: React.RefObject<HTMLInputElement>;
  handlePanFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removePanFile: () => void;
  removeGstinFile: () => void;
  onDocumentsChange: (docs: UploadingDocument[]) => void;
  supportingDocs: UploadingDocument[];
  remarks: string;
  onRemarksChange: (value: string) => void;
  existingDocs: { type: string; url: string }[];
  onExistingDocDownload: (docType: string) => void;
  removeExistingPanDoc: () => void;
  selectedBusinessType: 'organisation' | 'individual';
  selectedLicenseType: string;
  onLicenseTypeChange: (value: string) => void;
  businessLicenseFile: File | null;
  businessLicenseFileRef: React.RefObject<HTMLInputElement>;
  handleBusinessLicenseFileSelect: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  removeBusinessLicenseFile: () => void;
  removeExistingBusinessLicenseDoc: () => void;
  orgName: string;
  setOrgName: (value: string) => void;
  certificateType: string;
  setCertificateType: (value: string) => void;
  onGstinChange: (value: string) => void;
  gstin: string;
  gstinError: string;
  gstinFile: File | null;
  gstinFileRef: React.RefObject<HTMLInputElement>;
  handleGstinFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const PanCardDetails = ({
  pan,
  onPanChange,
  panError,
  panFile,
  panFileRef,
  handlePanFileSelect,
  removePanFile,
  removeGstinFile,
  onDocumentsChange,
  supportingDocs,
  remarks,
  onRemarksChange,
  existingDocs,
  onExistingDocDownload,
  removeExistingPanDoc,
  selectedBusinessType,
  selectedLicenseType,
  onLicenseTypeChange,
  businessLicenseFile,
  businessLicenseFileRef,
  handleBusinessLicenseFileSelect,
  removeBusinessLicenseFile,
  removeExistingBusinessLicenseDoc,
  orgName,
  setOrgName,
  certificateType,
  setCertificateType,
  onGstinChange,
  gstin,
  gstinError,
  gstinFile,
  gstinFileRef,
  handleGstinFileSelect
}: PanCardDetailsProps) => (
  <div>
    {selectedBusinessType === 'organisation' && (
      <div className="mb-4">
        <Label label="Business name" className="!mb-1" isRequired />
        <Input
          value={orgName}
          handleInputChange={setOrgName}
          placeholder="Enter business name"
          maxLength={NAME_MAX_LENGTH}
          inputRegex={nameRegex}
        />
      </div>
    )}

    <PanDocument
      pan={pan}
      onPanChange={onPanChange}
      panError={panError}
      panFile={panFile}
      panFileRef={panFileRef}
      handlePanFileSelect={handlePanFileSelect}
      removePanFile={removePanFile}
      existingDocs={existingDocs}
      onDownload={(type) => onExistingDocDownload(type)}
      removeExistingPanDoc={removeExistingPanDoc}
      selectedBusinessType={selectedBusinessType}
      certificateType={certificateType}
      setCertificateType={setCertificateType}
      onGstinChange={onGstinChange}
      gstin={gstin}
      gstinError={gstinError}
      gstinFile={gstinFile}
      gstinFileRef={gstinFileRef}
      handleGstinFileSelect={handleGstinFileSelect}
      removeGstinFile={removeGstinFile}
    />

    {selectedBusinessType === 'organisation' && (
      <div className="mt-4">
        <BusinessLicenseDocument
          selectedLicenseType={selectedLicenseType}
          onLicenseTypeChange={onLicenseTypeChange}
          businessLicenseFile={businessLicenseFile}
          businessLicenseFileRef={businessLicenseFileRef}
          handleBusinessLicenseFileSelect={handleBusinessLicenseFileSelect}
          removeBusinessLicenseFile={removeBusinessLicenseFile}
          existingDocs={existingDocs}
          onDownload={() => onExistingDocDownload('BUSINESS_LICENSE')}
          removeExistingBusinessLicenseDoc={removeExistingBusinessLicenseDoc}
        />
      </div>
    )}

    <div className="mt-4">
      <Label label="Supporting documents" className="!mb-3" />
      <SupportingDocumentUpload
        initialDocuments={supportingDocs.map((doc) => doc.file)}
        onDocumentsChange={onDocumentsChange}
        simulateUpload
        existingDocs={existingDocs}
      />
    </div>

    <div className="mt-4">
      <Label label="Remarks (if any)" className="!mb-1" />
      <TextArea
        value={remarks}
        onChange={onRemarksChange}
        placeholder="Type here..."
        maxLength={REMARKS_MAX_LENGTH}
        showCount
        rows={3}
      />
    </div>
  </div>
);

export default PanCardDetails;
